import { useState, useMemo, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Search,
  X,
  Clock,
  Calendar,
  User,
  ShieldCheck,
  ArrowRight,
  HelpCircle,
  Loader2,
  Sparkles,
} from "lucide-react";
import { getArticleBySlug } from "@/lib/blog.functions";
import { getBlogCoverImage } from "@/lib/blog-images";
import { MarkdownBody } from "@/components/blog/MarkdownBody";
import { Reveal } from "@/components/Reveal";

function readingTime(text?: string | null) {
  if (!text) return 3;
  const words = text.split(/\s+/).length;
  return Math.max(2, Math.round(words / 220));
}

function formatDate(iso?: string | null) {
  if (!iso) return "Recent";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "Recent";
  }
}

export type BlogCategory = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
};

export type ArticleSummary = {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  category_id?: string | null;
  category_name?: string | null;
  category_slug?: string | null;
  author_name?: string | null;
  reviewer_name?: string | null;
  published_at?: string | null;
  last_updated_at?: string | null;
  seo_score?: number | null;
};

export function BlogSplitView({
  categories,
  articles,
  initialCategorySlug = "all",
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
}: {
  categories: BlogCategory[];
  articles: ArticleSummary[];
  initialCategorySlug?: string | null;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}) {
  const [activeCategorySlug, setActiveCategorySlug] = useState<string>(
    initialCategorySlug || "all",
  );
  const [search, setSearch] = useState("");
  const [activeArticleSlug, setActiveArticleSlug] = useState<string | null>(null);

  // Sync initialCategorySlug if route changes
  useEffect(() => {
    setActiveCategorySlug(initialCategorySlug || "all");
  }, [initialCategorySlug]);

  const filteredArticles = useMemo(() => {
    let list = articles;

    if (activeCategorySlug !== "all") {
      list = list.filter(
        (a) =>
          a.category_slug === activeCategorySlug ||
          a.category_id === activeCategorySlug ||
          a.category_name?.toLowerCase() === activeCategorySlug.toLowerCase(),
      );
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (a) =>
          a.title?.toLowerCase().includes(q) ||
          a.excerpt?.toLowerCase().includes(q) ||
          a.category_name?.toLowerCase().includes(q),
      );
    }

    return list;
  }, [articles, activeCategorySlug, search]);

  // Select first article automatically when filtered list changes or initial load
  useEffect(() => {
    if (!activeArticleSlug && filteredArticles.length > 0) {
      setActiveArticleSlug(filteredArticles[0].slug);
    } else if (
      activeArticleSlug &&
      filteredArticles.length > 0 &&
      !filteredArticles.some((a) => a.slug === activeArticleSlug)
    ) {
      setActiveArticleSlug(filteredArticles[0].slug);
    }
  }, [filteredArticles, activeArticleSlug]);

  const activeSummary = useMemo(() => {
    return (
      articles.find((a) => a.slug === activeArticleSlug) ||
      filteredArticles[0] ||
      null
    );
  }, [articles, filteredArticles, activeArticleSlug]);

  // Fetch full article content dynamically
  const { data: fullArticleRes, isLoading: isArticleLoading } = useQuery({
    queryKey: ["blog-article-full", activeSummary?.slug],
    queryFn: () => getArticleBySlug({ data: { slug: activeSummary!.slug } }),
    enabled: !!activeSummary?.slug,
    staleTime: 5 * 60 * 1000,
  });

  const fullArticle = fullArticleRes?.article || activeSummary;
  const coverImage = getBlogCoverImage(fullArticle || activeSummary);
  const minutes = readingTime(
    (fullArticle as any)?.content || activeSummary?.excerpt,
  );
  const publishedDate = formatDate(
    activeSummary?.published_at || (fullArticle as any)?.published_at,
  );
  const faq = ((fullArticle as any)?.faq_json ?? []) as Array<{
    q: string;
    a: string;
  }>;

  return (
    <div className="grid gap-6 lg:grid-cols-[380px_minmax(0,1fr)] items-start">
      {/* Left Sidebar List */}
      <aside className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-110px)] flex flex-col border border-border bg-card rounded-md overflow-hidden shadow-sm">
        {/* Search & Filters */}
        <div className="p-4 border-b border-border bg-muted/20">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              placeholder="Search articles, topics, keywords"
              className="w-full h-10 pl-9 pr-8 text-[13px] bg-background border border-border rounded-md focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-sm hover:bg-muted text-muted-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Category Chips */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              onClick={() => setActiveCategorySlug("all")}
              className={`h-7 px-2.5 text-[11px] rounded transition-colors whitespace-nowrap font-medium ${
                activeCategorySlug === "all"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-background text-foreground/75 border border-border hover:border-primary/50 hover:text-foreground"
              }`}
            >
              All
            </button>
            {categories.map((c) => {
              const isActive = activeCategorySlug === c.slug;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveCategorySlug(c.slug)}
                  className={`h-7 px-2.5 text-[11px] rounded transition-colors whitespace-nowrap font-medium ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "bg-background text-foreground/75 border border-border hover:border-primary/50 hover:text-foreground"
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>

          <p className="mt-3 text-[11px] text-muted-foreground font-medium flex items-center justify-between">
            <span>
              {filteredArticles.length} of {articles.length} articles
            </span>
            {activeCategorySlug !== "all" && (
              <button
                onClick={() => setActiveCategorySlug("all")}
                className="text-primary hover:underline text-[10.5px]"
              >
                Reset filter
              </button>
            )}
          </p>
        </div>

        {/* Scrollable Article Items */}
        <div className="flex-1 overflow-y-auto divide-y divide-border max-h-[560px] lg:max-h-[calc(100vh-280px)]">
          {filteredArticles.length === 0 ? (
            <div className="p-8 text-center text-[13px] text-muted-foreground">
              No articles found matching your criteria.
            </div>
          ) : (
            filteredArticles.map((item) => {
              const isSelected = item.slug === activeSummary?.slug;
              const itemImg = getBlogCoverImage(item);
              const itemMinutes = readingTime(item.excerpt);
              const itemDate = formatDate(item.published_at);

              return (
                <button
                  key={item.id}
                  onClick={() => setActiveArticleSlug(item.slug)}
                  className={`w-full text-left p-4 flex gap-3.5 items-start transition-all border-l-[3px] ${
                    isSelected
                      ? "bg-primary/5 border-l-primary shadow-inner"
                      : "hover:bg-muted/50 border-l-transparent"
                  }`}
                >
                  <img
                    src={itemImg}
                    alt=""
                    className="h-14 w-14 object-cover flex-shrink-0 bg-muted rounded shadow-sm border border-border/40"
                    loading="lazy"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
                      <span className="text-primary font-bold">
                        {item.category_name || "Healthcare"}
                      </span>
                      {typeof item.seo_score === "number" &&
                        item.seo_score >= 85 && (
                          <span className="inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400 font-medium">
                            <Sparkles className="h-2.5 w-2.5" /> Featured
                          </span>
                        )}
                    </div>
                    <h3
                      className={`mt-1 text-[13px] font-semibold leading-snug line-clamp-2 ${
                        isSelected ? "text-primary" : "text-foreground"
                      }`}
                    >
                      {item.title}
                    </h3>
                    <div className="mt-1.5 flex items-center gap-2 text-[10.5px] text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>{itemMinutes} min read</span>
                      <span>·</span>
                      <span>{itemDate}</span>
                    </div>
                  </div>
                </button>
              );
            })
          )}

          {/* Load More Button inside list if paginated */}
          {onLoadMore && hasNextPage && (
            <div className="p-4 bg-muted/10 text-center">
              <button
                onClick={onLoadMore}
                disabled={isFetchingNextPage}
                className="w-full h-9 inline-flex items-center justify-center gap-2 text-[12.5px] font-medium border border-border bg-background hover:bg-muted transition-colors rounded-md disabled:opacity-50 shadow-sm"
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading more...
                  </>
                ) : (
                  "Load more articles"
                )}
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Right Column Reading Pane */}
      <main className="border border-border bg-background rounded-md overflow-hidden shadow-sm min-h-[600px] flex flex-col">
        {!activeSummary ? (
          <div className="p-16 text-center text-muted-foreground flex-1 flex flex-col items-center justify-center">
            <HelpCircle className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-base font-medium text-foreground">Select an article to start reading</p>
            <p className="text-sm mt-1">Choose any guide from the left sidebar.</p>
          </div>
        ) : (
          <article className="flex flex-col flex-1">
            {/* Hero Banner Cover */}
            <div className="aspect-[21/9] w-full overflow-hidden bg-muted relative">
              <img
                src={coverImage}
                alt={activeSummary.title}
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60" />
            </div>

            {/* Content Body */}
            <div className="p-6 sm:p-8 lg:p-10 max-w-[820px] mx-auto w-full flex-1 flex flex-col">
              <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-widest font-semibold">
                <span className="text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                  {activeSummary.category_name || "Healthcare"}
                </span>
                <span className="text-muted-foreground inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live preview
                </span>
              </div>

              <h1 className="mt-3.5 text-[clamp(1.5rem,2.6vw,2.25rem)] font-bold tracking-[-0.025em] leading-[1.15] text-foreground">
                {(fullArticle as any)?.h1 || activeSummary.title}
              </h1>

              {activeSummary.excerpt && (
                <p className="mt-3 text-[16px] text-muted-foreground leading-relaxed font-normal">
                  {activeSummary.excerpt}
                </p>
              )}

              {/* Meta info strip */}
              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-muted-foreground border-y border-border/60 py-3">
                <span className="text-foreground font-medium inline-flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-primary" />
                  {activeSummary.author_name || "Heallexa Editorial Team"}
                </span>
                <span>·</span>
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> Updated {publishedDate}
                </span>
                <span>·</span>
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <Clock className="h-3.5 w-3.5 text-primary" /> {minutes} min read
                </span>
                {(activeSummary.reviewer_name || (fullArticle as any)?.reviewer_name) && (
                  <>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                      <ShieldCheck className="h-3.5 w-3.5" /> Reviewed by{" "}
                      {activeSummary.reviewer_name || (fullArticle as any)?.reviewer_name}
                    </span>
                  </>
                )}
              </div>

              {/* Key Takeaways Box */}
              {activeSummary.excerpt && (
                <div className="mt-6 border-l-[3px] border-primary bg-primary/5 p-5 rounded-r-md">
                  <p className="text-[11px] uppercase tracking-widest text-primary font-bold mb-2 flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> Key takeaways
                  </p>
                  <ul className="space-y-2 text-[13.5px] text-foreground/90 font-normal">
                    <li className="flex gap-2.5 items-start">
                      <span className="text-primary font-bold mt-0.5">•</span>
                      <span>
                        Verify provider specialties, insurance acceptance, and clinical experience before scheduling care.
                      </span>
                    </li>
                    <li className="flex gap-2.5 items-start">
                      <span className="text-primary font-bold mt-0.5">•</span>
                      <span>
                        {activeSummary.excerpt}
                      </span>
                    </li>
                    <li className="flex gap-2.5 items-start">
                      <span className="text-primary font-bold mt-0.5">•</span>
                      <span>
                        Use Heallexa's verified directory to book appointments and compare healthcare costs instantly.
                      </span>
                    </li>
                  </ul>
                </div>
              )}

              {/* Full Markdown Body */}
              <div className="mt-8 flex-1">
                {isArticleLoading && !(fullArticle as any)?.content ? (
                  <div className="space-y-4 py-8 animate-pulse">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-5/6" />
                    <div className="h-32 bg-muted rounded w-full my-6" />
                    <div className="h-4 bg-muted rounded w-4/5" />
                    <div className="h-4 bg-muted rounded w-full" />
                  </div>
                ) : (fullArticle as any)?.content ? (
                  <div className="text-[15.5px] leading-[1.8] text-foreground/90 prose-styles">
                    <MarkdownBody source={(fullArticle as any).content} />
                  </div>
                ) : (
                  <div className="py-12 text-center text-muted-foreground text-sm card-surface-flat rounded-md">
                    Full article content loading or not yet published. Click "Read the full article" below for details.
                  </div>
                )}
              </div>

              {/* FAQ Section if available */}
              {faq.length > 0 && (
                <Reveal as="section" className="mt-14 border-t border-border pt-8">
                  <div className="flex items-center gap-2 mb-5">
                    <HelpCircle className="w-4 h-4 text-primary" />
                    <h3 className="text-[19px] font-bold tracking-tight text-foreground">
                      Frequently asked questions
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {faq.map((f, i) => (
                      <div key={i} className="card-surface-flat rounded-md p-4 bg-muted/20">
                        <p className="text-[14px] font-semibold text-foreground leading-snug">
                          {f.q}
                        </p>
                        <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                          {f.a}
                        </p>
                      </div>
                    ))}
                  </div>
                </Reveal>
              )}

              {/* Bottom Action Strip - Lexoor style CTA buttons */}
              <div className="mt-auto pt-8 border-t border-border flex flex-wrap items-center gap-3">
                <Link
                  to="/blog/article/$slug"
                  params={{ slug: activeSummary.slug }}
                  className="inline-flex h-11 items-center gap-2 px-6 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors rounded-md shadow-sm text-[13.5px]"
                >
                  Read the full article
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/search"
                  search={{ q: activeSummary.category_name || "doctor" }}
                  className="inline-flex h-11 items-center px-6 border border-border bg-background text-foreground font-semibold hover:bg-muted transition-colors rounded-md shadow-sm text-[13.5px]"
                >
                  Find a {activeSummary.category_name || "Healthcare"} provider
                </Link>
              </div>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
